import React from 'react';
import PropTypes from 'prop-types';
import {
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Alert,
  Snackbar,
  CircularProgress
} from '@mui/material';
import { useGlobalPromptContext } from '../../../hooks/useGlobalPromptContext';

/**
 * GlobalPromptContextSection - Textarea for managing the Global Prompt Context
 * on a rule set.
 *
 * Displays a multi-line text field with a live character counter, save button,
 * and success/error feedback. Follows the AutoExportSettingsSection pattern.
 *
 * Requirements: 2.1, 2.2, 2.3, 2.4, 2.5, 2.6, 2.7
 *
 * @param {string} rulesetId - The ID of the rule set
 */
const GlobalPromptContextSection = ({ rulesetId }) => {
  const {
    globalPromptContext,
    setGlobalPromptContext,
    remainingChars,
    loading,
    saving,
    error,
    success,
    saveGlobalPromptContext,
    clearError,
    clearSuccess
  } = useGlobalPromptContext(rulesetId);

  if (loading) {
    return (
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      </Paper>
    );
  }

  const isAtLimit = remainingChars <= 0;

  return (
    <>
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Global Prompt Context
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Prepended system instructions applied to every extraction using this rule set.
        </Typography>

        {error && (
          <Alert severity="error" onClose={clearError} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          multiline
          minRows={4}
          maxRows={12}
          fullWidth
          value={globalPromptContext}
          onChange={(e) => setGlobalPromptContext(e.target.value)}
          placeholder="Optional: Enter system-level extraction instructions..."
          inputProps={{ maxLength: 16000 }}
          disabled={saving}
          sx={{ mb: 1 }}
        />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="caption" color={isAtLimit ? 'error' : 'text.secondary'}>
            {remainingChars.toLocaleString()} / 16,000 characters remaining
          </Typography>
          {isAtLimit && (
            <Typography variant="caption" color="error">
              Character limit reached (16,000 maximum).
            </Typography>
          )}
        </Box>

        <Button
          variant="contained"
          onClick={() => saveGlobalPromptContext(globalPromptContext)}
          disabled={saving}
          size="small"
        >
          {saving ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
          Save
        </Button>
      </Paper>

      {/* Success Snackbar */}
      <Snackbar
        open={!!success}
        autoHideDuration={3000}
        onClose={clearSuccess}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={clearSuccess} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
    </>
  );
};

GlobalPromptContextSection.propTypes = {
  rulesetId: PropTypes.string.isRequired
};

export default GlobalPromptContextSection;
